import dayjs from 'dayjs'
import {MailerFilterField, MailerFormValidationSchema, SendMailPayload} from '@/features/mailer/types'

export const buildRecipientsPayload = (formData: MailerFormValidationSchema): SendMailPayload['recipients'] => {
    const recipients: SendMailPayload['recipients'] = {}

    if (formData.includePatients) {
        const fields: MailerFilterField[] = []

        fields.push({key: 'projectStatus', operator: 'in', value: ['opening', 'open']})

        if (formData.patientMinAge && formData.patientMinAge > 0) {
            const maxBirthDate = dayjs().subtract(formData.patientMinAge, 'year').format('YYYY-MM-DD')
            fields.push({key: 'birthDate', operator: '<=', value: maxBirthDate})
        }
        if (formData.patientMaxAge && formData.patientMaxAge > 0) {
            const minBirthDate = dayjs().subtract(formData.patientMaxAge, 'year').format('YYYY-MM-DD')
            fields.push({key: 'birthDate', operator: '>=', value: minBirthDate})
        }
        if (formData.patientGender) {
            fields.push({key: 'gender', operator: '=', value: formData.patientGender})
        }
        if (formData.patientResidenceAslDistrictId) {
            fields.push({key: 'residenceAslDistrictId', operator: '=', value: formData.patientResidenceAslDistrictId})
        }
        if (formData.patientOutsideRegion) {
            fields.push({key: 'domicileAslDistrictId', operator: 'is', value: null})
        }
        if (formData.patientNeuromuscularDiagnosisId) {
            fields.push({
                key: 'neuromuscularDiagnosisId',
                operator: '=',
                value: formData.patientNeuromuscularDiagnosisId
            })
        }
        if (formData.patientServiceType) {
            fields.push({
                key: 'serviceType',
                operator: '=',
                value: formData.patientServiceType
            })
        }
        // Only include patients who have an email
        fields.push({key: 'email', operator: 'is not', value: null})

        recipients.patients = {
            filters: fields.length > 0 ? {operator: 'and', fields} : undefined
        }
    }

    if (formData.includeConsultants) {
        const fields: MailerFilterField[] = []

        if (formData.consultantStatus) {
            fields.push({key: 'status', operator: '=', value: formData.consultantStatus})
        }

        if (formData.consultantSpecializationId) {
            fields.push({
                key: 'specializationId',
                operator: '=',
                value: formData.consultantSpecializationId
            })
        }
        if (formData.consultantContractType) {
            fields.push({
                key: 'contractType',
                operator: '=',
                value: formData.consultantContractType
            })
        }
        if (formData.consultantWorkSector) {
            fields.push({key: 'workSector', operator: '=', value: formData.consultantWorkSector})
        }
        // Only include consultants who have an email
        fields.push({key: 'email', operator: 'is not', value: null})

        recipients.consultants = {
            filters: fields.length > 0 ? {operator: 'and', fields} : undefined
        }
    }

    return recipients
}

const escapeHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export const plainTextBodyToHtml = (text: string): string =>
    text
        .split(/\n{2,}/)
        .map(block => `<p>${escapeHtml(block).replace(/\n/g, '<br>')}</p>`)
        .join('')

export const formatBytes = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
